import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import { EntityLink } from "scmp-app/components/entity-link";
import { useMyDailyFiveArticle } from "scmp-app/components/my-news/my-daily-five/hooks";
import type { secondaryArticleContent$key } from "scmp-app/queries/__generated__/secondaryArticleContent.graphql";

import {
  ActionBar,
  Container,
  ContentWrapper,
  Cover,
  CoverEntityLink,
  Headline,
  StyledNumber,
  TextWrapper,
  TopicLink,
  Wrapper,
} from "./styles";

export type Props = {
  className?: string;
  content: secondaryArticleContent$key;
  number: number | string;
  onClick?: (entityId?: string) => void;
};

export const Component = forwardRef<HTMLDivElement, Props>(
  ({ className, content: content_, number, onClick }, reference) => {
    const content = useFragment(
      graphql`
        fragment secondaryArticleContent on Content {
          ...entityLink
          ...helpersUseContentSponsorContent
          ...hooksUseMyDailyFiveContent
          entityId
          ...hooksContentItemProvidersContent
            @arguments(
              withTopicLink: true
              withHeadline: true
              withCoverImage: true
              withCoverImageSize540x360: true
              withCoverImageSize118x118: true
              withActionBar: true
            )
        }
      `,
      content_,
    );

    const isTrendingVisible = useMyDailyFiveArticle(content);
    const providers = useContentItemProviders(content, { type: "main" });

    return (
      <Container className={className} ref={reference}>
        <StyledNumber number={number} />
        <Wrapper>
          <ContentWrapper>
            <TextWrapper>
              <TopicLink>{providers.topicLink?.()}</TopicLink>
              <EntityLink onClick={() => onClick?.(content.entityId)} reference={content}>
                <Headline>{providers.headline({ preferSocialHeadline: true })}</Headline>
              </EntityLink>
            </TextWrapper>

            <CoverEntityLink onClick={() => onClick?.(content.entityId)} reference={content}>
              <EntityLink onClick={() => onClick?.(content.entityId)} reference={content}>
                <Cover>
                  {providers.coverImage({
                    responsiveVariants: {
                      desktopUp: "size540x360",
                      mobileUp: "size118x118",
                      tabletUp: "size540x360",
                    },
                  })}
                </Cover>
              </EntityLink>
            </CoverEntityLink>
          </ContentWrapper>

          <ActionBar>
            {providers.actionBar({
              isIconVisible: false,
              isTrendingVisible,
            })}
          </ActionBar>
        </Wrapper>
      </Container>
    );
  },
);

Component.displayName = "SecondaryArticle";

export const SecondaryArticle = withHighlightedHeadline(Component);
